import { useCallback } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PUSH_SKU_RULES_CAP, PUSH_SKU_RULES_CAP_EXCEEDED } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { messageOf, readCauseCode, trpcErrorCode } from '@/lib/trpc-error-utils';
import { undoCalls, type PreviousRule, type PushSkuRuleOrNone } from '../utils/push-sku-rules';

/** One id, so a repeated failure replaces its predecessor instead of stacking. */
const ERROR_TOAST_ID = 'push-sku-rules-error';
/**
 * A success toast that carries Undo stays a little longer than the 4 s default
 * (DESIGN.md 15.5), so the button can be reached after reading it.
 */
const UNDO_TOAST_DURATION_MS = 10_000;

/**
 * The cap refusal reads in the operator's language through the shared cause
 * code. Any other refusal (BAD_REQUEST: the cap when the cause did not reach
 * the browser, a stale list) carries a sentence the server wrote for the
 * operator, so it is shown as it is. Anything else gets our lead-in, or a
 * plain retry line when there is no detail at all.
 */
function errorMessage(error: unknown, t: TFunction<'inventory'>): string {
  if (readCauseCode(error) === PUSH_SKU_RULES_CAP_EXCEEDED) {
    return t('stockSync.pushRules.error.capExceeded', { cap: PUSH_SKU_RULES_CAP.toLocaleString() });
  }
  const detail = messageOf(error).trim();
  if (trpcErrorCode(error) === 'BAD_REQUEST' && detail) return detail;
  return detail
    ? t('stockSync.pushRules.error.failed', { message: detail })
    : t('stockSync.pushRules.error.failedNoDetail');
}

/**
 * The one write path for Skip / Always 0 / Remove, shared by the card's search
 * rows, its list, its selection bar and the paste dialog, so every caller
 * toasts the same words and refreshes the same list.
 *
 * `apply` resolves `true` once the list has been refetched (callers close a
 * dialog onto fresh rows, DESIGN.md 12.2), `false` on failure. Failures toast
 * and stay until dismissed (DESIGN.md 15.5); nothing throws to the caller.
 *
 * Every change that moved a SKU offers Undo on its toast. Undo puts back the
 * rules the server reported before the change (`previous`), one call per
 * rule, with no confirm: it restores a state someone already confirmed.
 */
export function useSetPushSkuRules() {
  const { t } = useTranslation('inventory');
  const utils = trpc.useUtils();
  const mutation = trpc.stockSync.setPushSkuRules.useMutation();
  const { mutateAsync } = mutation;

  const failed = useCallback(
    (error: unknown) => {
      toast.error(errorMessage(error, t), { id: ERROR_TOAST_ID, duration: Infinity });
    },
    [t],
  );

  const undo = useCallback(
    async (previous: readonly PreviousRule[]) => {
      try {
        for (const call of undoCalls(previous)) await mutateAsync(call);
        await utils.stockSync.listPushSkuRules.invalidate();
        toast.dismiss(ERROR_TOAST_ID);
        toast.success(t('stockSync.pushRules.toast.undone', { count: previous.length }));
      } catch (error) {
        // A part-way undo still shows what was put back.
        void utils.stockSync.listPushSkuRules.invalidate();
        failed(error);
      }
    },
    [failed, mutateAsync, t, utils],
  );

  const apply = useCallback(
    async (skuIds: string[], rule: PushSkuRuleOrNone): Promise<boolean> => {
      if (skuIds.length === 0) return false;
      try {
        const result = await mutateAsync({ skuIds, rule });
        // Refetch before the toast so the table already shows the change when
        // the words arrive, and a dialog awaiting this closes onto fresh rows.
        await utils.stockSync.listPushSkuRules.invalidate();
        const changed = result.changed;
        const ignoredLine =
          result.ignored > 0 ? t('stockSync.pushRules.toast.ignored', { count: result.ignored }) : undefined;
        let title: string;
        let description = ignoredLine;
        if (changed === 0 && ignoredLine) {
          // Nothing landed because the SKUs cannot take a rule (a pre-printed
          // design, a deleted SKU): say that, never "already set that way".
          title = ignoredLine;
          description = undefined;
        } else if (changed === 0) {
          title = t('stockSync.pushRules.toast.nothingChanged');
        } else if (rule === 'skip') {
          title = t('stockSync.pushRules.toast.setSkip', { count: changed });
        } else if (rule === 'always_zero') {
          title = t('stockSync.pushRules.toast.setAlwaysZero', { count: changed });
        } else {
          title = t('stockSync.pushRules.toast.removed', { count: changed });
        }
        const previous = result.previous;
        const canUndo = changed > 0 && previous.length > 0;
        toast.dismiss(ERROR_TOAST_ID);
        toast.success(title, {
          description,
          ...(canUndo
            ? {
                duration: UNDO_TOAST_DURATION_MS,
                action: { label: t('stockSync.pushRules.toast.undo'), onClick: () => void undo(previous) },
              }
            : {}),
        });
        return true;
      } catch (error) {
        failed(error);
        return false;
      }
    },
    [failed, mutateAsync, t, undo, utils],
  );

  return {
    apply,
    isPending: mutation.isPending,
  };
}
